import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useEventTracking } from 'react-event-tracker';
import { Text, Container, Grid, Button, Callout } from 'basis';
import { useStoreValue, AXIOS_UNKNOWN_ERROR, OKTA_OTP_TOO_OFTEN_ERROR, OKTA_SMS_FACTOR } from 'store';
import { LayoutPage, LayoutContent, Form, Errors, Modal, Nudge } from 'components';
import { isFeatureOn } from 'featureToggles';
import messageLabels from 'components/message/messageLabels.json';
import { initFormConfig, submitHandler, getDataLayerElements } from 'utils';
import {
  useErrorTrackingAndUpdateStore,
  useFormInitialValues,
  useRegisterAndLoginUser,
  useSteps,
  useThreatMetrix,
} from 'hooks';
import { config, STEP_VERIFY_TO_RESUME, STEP_VERIFY } from '_config';
import { DEV_FEATURES } from '__dev/devFeatures';
import { OktaProfileError } from 'components/errors/OktaProfileError';
import { VStack } from '@chakra-ui/react';
import { ContactDetailsFields } from './ContactDetailsFields';
import { useFocusOnFirstFormElement } from '../../hooks/useFocusOnFirstFormElement';

const FORM_ID = 'contactDetails';

export function ContactDetails() {
  const [storeState, updateStore] = useStoreValue();
  const { isResuming, acquisition, applicationStatus, ...state } = storeState;
  const { pathname } = useSteps();
  const [showMobileModal, setShowMobileModal] = useState(false);

  useThreatMetrix({ disabled: !isResuming });
  const handleSubmitSuccess = () => {
    updateStore({ activeStep: nextStep, applicationErrors: null });
    history.push(nextStep);
  };
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore();

  const { loading, submit } = useRegisterAndLoginUser({
    onSuccess: handleSubmitSuccess,
    onError: handleErrorCallback,
  });

  const retrySubmit = useCallback(() => {
    const { emailAddress: email, mobileNumber: mobile } = acquisition.contactDetails;
    submit({ email, mobile });
  }, [acquisition.contactDetails, submit]);

  const nextStep = useMemo(() => {
    return isResuming ? STEP_VERIFY_TO_RESUME : STEP_VERIFY;
  }, [isResuming]);

  const formConfig = initFormConfig(config.formConfigs[FORM_ID](isResuming));

  const history = useHistory();
  const { trackEvent } = useEventTracking();
  const initialValues = useFormInitialValues(FORM_ID);

  useFocusOnFirstFormElement();

  const popupMobileConfirmationModal = useCallback(
    formState => {
      const hasValidationErrors = Object.keys(formState?.errors || {}).length > 0;
      if (hasValidationErrors) {
        return;
      }
      trackEvent({
        event: {
          category: 'application',
          action: 'interaction',
          location: pathname.slice(1),
          label: 'Next',
        },
      });
      setShowMobileModal(true);
    },
    [pathname, trackEvent],
  );

  const closeMobileConfirmationModal = useCallback(() => {
    setShowMobileModal(false);
  }, [setShowMobileModal]);

  const onSubmit = ({ values, errors }) => {
    closeMobileConfirmationModal();

    function registerAndLoginUser() {
      const uniqueEmail = DEV_FEATURES.OTP_GENERATE_UNIQUE_EMAIL
        ? values.emailAddress.replace(/\d*@/, `${Date.now()}@`)
        : '';

      const contactDetails = {
        ...values,
        ...(uniqueEmail
          ? {
              emailAddress: uniqueEmail,
              confirmEmail: uniqueEmail,
            }
          : null),
      };

      const newAcquisition = {
        ...acquisition,
        contactDetails,
      };

      updateStore({
        oktaFactor: OKTA_SMS_FACTOR,
        acquisition: newAcquisition,
      });

      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: pathname.slice(1),
          label: isFeatureOn('mobileConfirmationModal') ? 'Confirm' : 'Next',
        },
        ...getDataLayerElements({ acquisition: newAcquisition, ...state }),
      });

      const { emailAddress: email, mobileNumber: mobile } = newAcquisition.contactDetails;

      if (DEV_FEATURES.OTP_SKIP) {
        updateStore({
          maskedMobileNumber: '04*******00',
        });
        handleSubmitSuccess();
      } else {
        submit({ email, mobile });
      }
    }

    submitHandler({ submit: registerAndLoginUser, errors });
  };

  const hasErrors = !!state.applicationErrors;

  const noProgressStepper = isResuming;

  if (hasErrors && [OKTA_OTP_TOO_OFTEN_ERROR, AXIOS_UNKNOWN_ERROR].includes(state.applicationErrors?.type)) {
    return (
      <LayoutPage noProgressStepper={noProgressStepper}>
        <Errors applicationRef={storeState.applicationRef} retry={retrySubmit} retrying={loading} isPreSubmission />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage noProgressStepper={noProgressStepper} hideTitleIfErrors={false}>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={isFeatureOn('mobileConfirmationModal') ? popupMobileConfirmationModal : onSubmit}
        loading={loading}
        submitButtonLabel="Next"
        disableFormCache
      >
        {formData => {
          return (
            <LayoutContent>
              <Callout>{messageLabels.contactDetails.blueMessage.body}</Callout>
              <ContactDetailsFields isResuming={isResuming} config={formConfig} formData={formData} />
              {hasErrors && <OktaProfileError />}
              <Nudge />
              <MobileConfirmationModal
                trackEvent={trackEvent}
                formData={formData}
                onClose={closeMobileConfirmationModal}
                isOpen={showMobileModal && !hasErrors}
                loading={loading}
                onSubmit={() => onSubmit({ values: formData.state.values, errors: formData.state.errors })}
              />
            </LayoutContent>
          );
        }}
      </Form>
    </LayoutPage>
  );
}

function MobileConfirmationModal({ formData, isOpen, onClose, onSubmit, loading, trackEvent }) {
  const onEdit = useCallback(() => {
    trackEvent({
      event: {
        category: 'application',
        action: 'interaction',
        location: 'contact-details',
        label: 'Edit',
      },
    });
    onClose();
  }, [onClose, trackEvent]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} style={{ maxWidth: 600 }}>
      <Container padding="9">
        <Text textStyle="heading5">Confirm mobile number</Text>
        <Container padding="9 0">
          <VStack spacing="24" alignItems="left">
            <Text>Please confirm the mobile number entered before you proceed as it cannot be changed.</Text>
            <Text>
              <strong>Mobile number: {formatMobileNumber(formData.state.values.mobileNumber)}</strong>
            </Text>
          </VStack>
        </Container>
        <Grid rowsGap="4" colsGap="2">
          <Grid.Item colSpan="all" colSpan-sm="0-1">
            <Grid>
              <Button testId="editButton" type="button" variant="secondary" onClick={onEdit} disabled={loading}>
                Edit
              </Button>
            </Grid>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="2-3" alignItems="center">
            <Grid>
              <Button onClick={onSubmit} loading={loading}>
                {loading ? 'Submitting' : 'Confirm'}
              </Button>
            </Grid>
          </Grid.Item>
        </Grid>
      </Container>
    </Modal>
  );
}

function formatMobileNumber(number) {
  const cleaned = number.replace(/\D/g, '');

  if (cleaned.length < 8) {
    return cleaned;
  }

  const firstPart = cleaned.substring(0, 4);
  const secondPart = cleaned.substring(4, 7);
  const thirdPart = cleaned.substring(7);

  return `${firstPart} ${secondPart} ${thirdPart}`;
}
